'use strict';

import React, { useState, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import Modal from 'react-modal';
import Select from 'react-select';
import cn from 'classnames/bind';

import { appSelectors, appOperations } from '@/state/domains/app';
import { usersSelectors, usersOperations } from '@/state/domains/users';
import {
	sanitize,
	runTests,
	hasError,
	triggerErrors,
	triggerServerErrors
} from '@/state/utilities/forms';

import Tooltip from '@/views/common/Tooltip';
import s from './styles.scss';
import m from '../styles.scss';

const sx = cn.bind(s);

const workWeekOpts = [
	'Monday—Friday',
	'Tuesday—Saturday',
	'Wednesday—Sunday',
	'Thursday—Monday',
	'Friday—Tuesday',
	'Saturday—Wednesday',
	'Sunday—Thursday',
];

const roleOpts = [
	{
		label: 'Manager',
		value: 'manager',
	},
	{
		label: 'Process Server',
		value: 'server',
	},
	{
		label: 'Messenger',
		value: 'messenger',
	}
];

const scopes = {
	general: {
		sanitize: (fieldConfigs, input) => {
			return sanitize(fieldConfigs, input);
		},
		validate: (fieldConfigs, input) => {
			return runTests(fieldConfigs, input);
		},
		fields: {
			workWeek: {
				sanitize: v => v,
				tests: [
					{
						test: v => !!v,
						message: 'Must choose one',
					},
				],
			},
		},
	},
	passphrase: {
		sanitize: (fieldConfigs, input) => {
			return sanitize(fieldConfigs, input);
		},
		validate: (fieldConfigs, input) => {

			const report = runTests(fieldConfigs, input);
			const reportM = Object.fromEntries(report);

			const { newPassphrase, confirmPassphrase } = input;

			if (newPassphrase !== confirmPassphrase) {
				reportM.confirmPassphrase.success = false;
				reportM.confirmPassphrase.testResults = [
					{
						success: false,
						message: 'Passphrase doesn\'t match',
					},
				];
			}

			return Object.entries(reportM);
		},
		fields: {
			currentPassphrase: {
				sanitize: v => v.trim(),
				tests: [
					{
						test: v => v && v.length,
						message: 'Please enter your current passphrase',
					},
				],
			},
			newPassphrase: {
				sanitize: v => v.trim(),
				tests: [
					{
						test: v => /^[ -~]{5,16}$/.test(v),
						message: 'Passphrases must be 5-16 printable characters',
					},
				],
			},
			confirmPassphrase: {
				sanitize: v => v.trim(),
				tests: [
				],
			},
		},
	},
};

export default connect(
	(state, props) => ({
		modals: appSelectors.getModalStates(state, props),
		username: usersSelectors.getUsername(state, props),
		user: usersSelectors.getLoggedInUser(state, props),
	}),
	{
		setModalClose: appOperations.setModalClose,
		postCreateUser: usersOperations.postCreateUser,
		changePassphrase: usersOperations.changePassphrase,
		postSettings: usersOperations.postSettings,
	}
)(
	({
		modals,
		username,
		user,
		setModalClose,
		postCreateUser,
		changePassphrase,
		postSettings,
	}) => {

		const [ activeTab, setActiveTab ] = useState('general');

		const [ currentPassphrase, setCurrentPassphrase ] = useState('');
		const [ newPassphrase, setNewPassphrase ] = useState('');
		const [ confirmPassphrase, setConfirmPassphrase ] = useState('');
		const [ workWeek, setWorkWeek ] = useState({
			label: user.settings.workWeek,
			value: user.settings.workWeek,
		});

		const [ currentPassphraseE, setCurrentPassphraseE ] = useState({
			error: false,
			message: ''
		});
		const [ newPassphraseE, setNewPassphraseE ] = useState({
			error: false,
			message: ''
		});
		const [ confirmPassphraseE, setConfirmPassphraseE ] = useState({
			error: false,
			message: ''
		});
		const [ workWeekE, setWorkWeekE ] = useState({
			error: false,
			message: ''
		});

		const onCurrentPassphraseChange = e => {
			setCurrentPassphrase(e.target.value);
			setCurrentPassphraseE({ error: false, message: '' });
		};

		const onNewPassphraseChange = e => {
			setNewPassphrase(e.target.value);
			setNewPassphraseE({ error: false, message: '' });
		};

		const onConfirmPassphraseChange = e => {
			setConfirmPassphrase(e.target.value);
			setConfirmPassphraseE({ error: false, message: '' });
		};

		const onWorkWeekChange = opt => {
			setWorkWeek(opt);
			setWorkWeekE({ error: false, message: '' });
		};

		const close = _user => {

			let workWeek;

			if (_user && _user.settings) {
				workWeek = _user.settings.workWeek;
			} else {
				workWeek = user.settings.workWeek;
			}

			setActiveTab('general');
			setCurrentPassphrase('');
			setNewPassphrase('');
			setWorkWeek({
				label: workWeek,
				value: workWeek,
			});

			setCurrentPassphraseE({ error: false, message: '' });
			setNewPassphraseE({ error: false, message: '' });
			setWorkWeekE({ error: false, message: '' });

			setModalClose('userSettings')
		};

		const uiErrorReporters = {
			general: {
				workWeek: {
					affirmative: messages => setWorkWeekE({
						error: true,
						message: messages[ 0 ].message,
					}),
					negative: () => setWorkWeekE({ error: false, message: '' }),
				},
			},
			passphrase: {
				currentPassphrase: {
					affirmative: messages => setCurrentPassphraseE({
						error: true,
						message: messages[ 0 ].message,
					}),
					negative: () => setCurrentPassphraseE({
						error: false,
						message: '',
					}),
				},
				newPassphrase: {
					affirmative: messages => setNewPassphraseE({
						error: true,
						message: messages[ 0 ].message,
					}),
					negative: () => setNewPassphraseE({
						error: false,
						message: '',
					}),
				},
				confirmPassphrase: {
					affirmative: messages => setConfirmPassphraseE({
						error: true,
						message: messages[ 0 ].message,
					}),
					negative: () => setConfirmPassphraseE({
						error: false,
						message: '',
					}),
				},
			},
		};

		const prepareData = _data => {

			const data = { ..._data };

			if ('workWeek' in _data) {
				data.workWeek = _data.workWeek.value;
			}
			return data;
		};

		const makeCallback = data => {

			const scope = scopes[ activeTab ];
			const errorReporter = uiErrorReporters[ activeTab ];

			return e => {

				e.preventDefault();

				data = prepareData(data);
				data = scope.sanitize(scope.fields, data);

				const validationReport = scope.validate(scope.fields, data);

				if (hasError(validationReport)) {
					triggerErrors(errorReporter, validationReport);

				} else {

					let updateFn;
					switch (activeTab) {
						case 'passphrase':
							updateFn = changePassphrase;
							break;
						default:
							updateFn = postSettings;
							break;
					}

					updateFn(username, data).then(r => {
						if (r.success) {
							close(r.data.user);
						} else {
							triggerServerErrors(errorReporter, r.data.errors);
						}
					}).catch(e => {
						triggerServerErrors(errorReporter, e.data.errors);
					});
				}
			}
		};

		const submits = {
			general: useCallback(
				makeCallback({
					workWeek,
				}),
				[ activeTab, workWeek ]
			),
			passphrase: useCallback(
				makeCallback({
					currentPassphrase,
					newPassphrase,
					confirmPassphrase,
				}),
				[ activeTab, currentPassphrase, newPassphrase, confirmPassphrase ]
			),
		};

		const tooltipInit = {
			placement: 'right',
			middleware: ({ arrowRef, arrow, offset }) => [
				offset(4),
				arrow({ element: arrowRef }),
			],
		};

		return (
			<Modal
				appElement={ document.getElementById('root') }
				overlayClassName={ m.modalOverlay }
				isOpen={ modals.userSettings }
				contentLabel='User Settings'
				shouldCloseOnOverlayClick={ true }
				shouldCloseOnEsc={ true }
				onRequestClose={ close }
				className={ m.modal }
			>
				<div className={ s.root }>
					<header className={ s.header }>
						Edit Settings
					</header>
					<div className={ s.tabsCont }>
						<div className={ s.tabs }>
							<ul className={ s.tabsUL }>
								<li
									className={
										sx(
											'tabBtn',
											{ active: activeTab === 'general'}
										)
									}
									onClick={ () => setActiveTab('general') }
								>
									General
								</li>
								<li
									className={
										sx(
											'tabBtn',
											{ active: activeTab === 'passphrase'}
										)
									}
									onClick={ () => setActiveTab('passphrase') }
								>
									Password
								</li>
							</ul>
						</div>
						<div className={ s.tabUICont }>
							<div className={ s.tabPanes }>
								<div
									className={
										sx(
											'tabPane',
											{
												active: activeTab === 'general'
											}
										)
									}
								>
									<div className={ s.formRow }>
										<label className={ s.label }>Work Week</label>
										<Tooltip
											init={ tooltipInit }
											show={ workWeekE && workWeekE.error }
											className={ s.errorTooltip }
											arrowClassName={ s.arrow }
											arrowStyle={{
												left: -3,
											}}
											content={ workWeekE.message }
										>
											<div className={ s.formCont }>
												<Select
													onChange={ onWorkWeekChange }
													options={
														workWeekOpts.map(
															o => ({ label: o, value: o })
														)
													}
													value={ workWeek }
													isClearable={ false }
													styles={
														{
															container: existing => ({
																...existing,
															}),
															control: (base, state) => ({
																...base,
																...( workWeekE.error ?
																	{
																		borderColor: '#DE350B',
																		borderWidth: '3px',
																	} :
																	{}
																)
															}),
														}
													}
												/>
											</div>
										</Tooltip>
									</div>
								</div>
								<div
									className={
										sx(
											'tabPane',
											{
												active: activeTab === 'passphrase'
											}
										)
									}
								>
									<form className={ s.form } >
										<div className={ s.formRow }>
											<label className={ s.label }>Current Passphrase</label>
											<Tooltip
												init={ tooltipInit }
												show={ currentPassphraseE && currentPassphraseE.error }
												className={ s.errorTooltip }
												arrowClassName={ s.arrow }
												arrowStyle={{
													left: -3,
												}}
												content={ currentPassphraseE.message }
											>
												<div className={ s.formCont }>
													<input
														type='password'
														className={ s.input }
														className={ sx('input', { error: currentPassphraseE.error }) }
														onChange={ onCurrentPassphraseChange }
														value={ currentPassphrase }
													/>
												</div>
											</Tooltip>
										</div>
										<div className={ s.formRow }>
											<label className={ s.label }>New Passphrase</label>
											<Tooltip
												init={ tooltipInit }
												show={ newPassphraseE && newPassphraseE.error }
												className={ s.errorTooltip }
												arrowClassName={ s.arrow }
												arrowStyle={{
													left: -3,
												}}
												content={ newPassphraseE.message }
											>
												<div className={ s.formCont }>
													<input
														type='password'
														className={ s.input }
														className={ sx('input', { error: newPassphraseE.error }) }
														onChange={ onNewPassphraseChange }
														value={ newPassphrase }
													/>
												</div>
											</Tooltip>
										</div>
										<div className={ s.formRow }>
											<label className={ s.label }>Confirm Passphrase</label>
											<Tooltip
												init={ tooltipInit }
												show={ confirmPassphraseE && confirmPassphraseE.error }
												className={ s.errorTooltip }
												arrowClassName={ s.arrow }
												arrowStyle={{
													left: -3,
												}}
												content={ confirmPassphraseE.message }
											>
												<div className={ s.formCont }>
													<input
														type='password'
														className={ s.input }
														className={ sx('input', { error: confirmPassphraseE.error }) }
														onChange={ onConfirmPassphraseChange }
														value={ confirmPassphrase }
													/>
												</div>
											</Tooltip>
										</div>
									</form>
								</div>
							</div>
							<div className={ s.btnSection }>
								<button
									type='submit'
									className={ s.submit }
									onClick={ submits[ activeTab ] }
								>
									Update
								</button>
							</div>
						</div>
						
					</div>
				</div>
			</Modal>
		);
	}
);
